import React from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import type { MayBe } from 'types/common';
import { AbstractMedia } from 'types/AbstractMedia';
import { SelfServiceType } from 'modules/selfServices/types/SelfService';

import useAnalytics from 'hooks/useAnalytics';
import { downloadURL } from 'utils/downloadUtils';
import useLabelTranslation from 'hooks/useLabelTranslation';
import { useGetSelfServicePdfMutation } from 'modules/selfServices/service';
import type { FinalInstructionsResponse } from 'modules/dealers/types/FinalInstructionsResponse';

import { getSelfServiceType } from 'modules/selfServices/selectors';

type UseDownloadFinalInstructionsDocumentsProps = {
  selfServiceId: string;
  instructions: MayBe<FinalInstructionsResponse>;
};

const useDownloadFinalInstructionsDocuments = ({ selfServiceId, instructions }: UseDownloadFinalInstructionsDocumentsProps) => {
  const intl = useIntl();
  const { onClick } = useAnalytics();
  const { getLabelTranslation } = useLabelTranslation();

  const type = useSelector(getSelfServiceType);

  const [downloadPdf, { isLoading: isDownloading }] = useGetSelfServicePdfMutation();

  const checkInSummary = React.useMemo(() => (type === SelfServiceType.CHECK_IN ? ({
    id: selfServiceId,
    filename: intl.formatMessage({
      id: 'page.finalInstructions.downloadPdf.filename',
      defaultMessage: 'Check-in summary',
    }),
  } as AbstractMedia) : null), [intl, selfServiceId, type]);

  const files = React.useMemo(() => [
    checkInSummary,
    ...(instructions?.documents ?? []),
  ].filter(Boolean), [checkInSummary, instructions]);

  const documentList = React.useMemo(() => files.map((file) => {
    if ('url' in file) {
      return ({
        id: file.url.split('/').pop(),
        filename: getLabelTranslation(file.name),
        path: file.url,
      } as AbstractMedia);
    }

    return file;
  }), [getLabelTranslation, files]);

  const handleDownload = React.useCallback((media: AbstractMedia) => {
    if (checkInSummary && media.id === checkInSummary.id) {
      onClick({ action: 'DownloadFile', category: 'FINAL_INSTRUCTIONS' }, downloadPdf)(media);
    } else {
      downloadURL(media.path, media.filename);
    }
  }, [checkInSummary, onClick, downloadPdf]);

  return React.useMemo(() => ({
    files,
    documentList,
    isDownloading,
    handleDownload,
  }), [files, documentList, handleDownload, isDownloading]);
};

export default useDownloadFinalInstructionsDocuments;
